import { ChevronLeft, ChevronRight, ExternalLink, GitMerge } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import type { ReleaseEntry, UxImpact } from '../types/release.js';
import { backlogUrl } from '../lib/backlog-url.js';

/** UX影響度バッジの配色。 @graph-connects none */
const UX_TINT: Record<UxImpact, string> = {
  ux_change: 'var(--color-accent-primary)',
  indirect: 'var(--color-text-secondary)',
  none: 'var(--color-text-muted)',
};

/** mergedAt(ISO) を JST の YYYY-MM-DD キーに変換する。 @graph-connects none */
function toJstDateKey(iso: string): string {
  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(iso));
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** 曜日ヘッダーの翻訳キー（月曜始まり）。 @graph-connects none */
const WEEKDAY_LABEL_KEYS = [
  'release.weekday.mon',
  'release.weekday.tue',
  'release.weekday.wed',
  'release.weekday.thu',
  'release.weekday.fri',
  'release.weekday.sat',
  'release.weekday.sun',
] as const;

interface CalendarDay {
  date: number;
  dateKey: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  entries: ReleaseEntry[];
}

/** 月カレンダーのグリッドデータ（4〜6週×7日）を生成する。 @graph-connects none */
function buildCalendarGrid(
  year: number,
  month: number,
  entryMap: Map<string, ReleaseEntry[]>,
): CalendarDay[][] {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDow = (firstDay.getDay() + 6) % 7;

  const todayKey = toJstDateKey(new Date().toISOString());
  const days: CalendarDay[] = [];

  const prevMonthLast = new Date(year, month, 0);
  for (let i = startDow - 1; i >= 0; i--) {
    const d = prevMonthLast.getDate() - i;
    const dateKey = `${String(prevMonthLast.getFullYear())}-${String(prevMonthLast.getMonth() + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    days.push({
      date: d,
      dateKey,
      isCurrentMonth: false,
      isToday: dateKey === todayKey,
      entries: entryMap.get(dateKey) ?? [],
    });
  }

  for (let d = 1; d <= lastDay.getDate(); d++) {
    const dateKey = `${String(year)}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    days.push({
      date: d,
      dateKey,
      isCurrentMonth: true,
      isToday: dateKey === todayKey,
      entries: entryMap.get(dateKey) ?? [],
    });
  }

  const remaining = 7 - (days.length % 7);
  if (remaining < 7) {
    const nextMonth = month + 1;
    const nextYear = nextMonth > 11 ? year + 1 : year;
    const nm = nextMonth > 11 ? 0 : nextMonth;
    for (let d = 1; d <= remaining; d++) {
      const dateKey = `${String(nextYear)}-${String(nm + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      days.push({
        date: d,
        dateKey,
        isCurrentMonth: false,
        isToday: dateKey === todayKey,
        entries: entryMap.get(dateKey) ?? [],
      });
    }
  }

  const weeks: CalendarDay[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  return weeks;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business リリース履歴のカレンダービュー。月カレンダーグリッドで日ごとのリリース件数を表示し、日クリックで詳細リストを展開する。
 * @graph-connects none
 */
export function ReleaseCalendarView({
  entries,
  categoryLabel,
  uxLabel,
}: {
  entries: ReleaseEntry[];
  categoryLabel: Map<string, string>;
  uxLabel: Map<string, string>;
}) {
  const { t } = useTranslation();
  const initialJstKey = toJstDateKey(
    entries.length > 0 ? entries[0].mergedAt : new Date().toISOString(),
  );
  const [viewYear, setViewYear] = useState(Number(initialJstKey.slice(0, 4)));
  const [viewMonth, setViewMonth] = useState(Number(initialJstKey.slice(5, 7)) - 1);
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);

  const entryMap = useMemo(() => {
    const map = new Map<string, ReleaseEntry[]>();
    for (const entry of entries) {
      const key = toJstDateKey(entry.mergedAt);
      const list = map.get(key);
      if (list) {
        list.push(entry);
      } else {
        map.set(key, [entry]);
      }
    }
    return map;
  }, [entries]);

  const weeks = useMemo(
    () => buildCalendarGrid(viewYear, viewMonth, entryMap),
    [viewYear, viewMonth, entryMap],
  );

  const selectedEntries = selectedDateKey ? (entryMap.get(selectedDateKey) ?? []) : [];

  const goToPrevMonth = () => {
    if (viewMonth === 0) {
      setViewYear(viewYear - 1);
      setViewMonth(11);
    } else {
      setViewMonth(viewMonth - 1);
    }
    setSelectedDateKey(null);
  };

  const goToNextMonth = () => {
    if (viewMonth === 11) {
      setViewYear(viewYear + 1);
      setViewMonth(0);
    } else {
      setViewMonth(viewMonth + 1);
    }
    setSelectedDateKey(null);
  };

  const monthLabel = t('release.monthLabel', {
    year: String(viewYear),
    month: String(viewMonth + 1),
  });

  return (
    <div className="mt-8">
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          onClick={goToPrevMonth}
          className="rounded-lg p-2 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-base)]"
          aria-label={t('release.prevMonth')}
        >
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <span className="text-lg font-semibold text-[var(--color-text-primary)]">{monthLabel}</span>
        <button
          type="button"
          onClick={goToNextMonth}
          className="rounded-lg p-2 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-base)]"
          aria-label={t('release.nextMonth')}
        >
          <ChevronRight className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <Card className="overflow-hidden p-0">
        <div className="grid grid-cols-7">
          {WEEKDAY_LABEL_KEYS.map((labelKey) => (
            <div
              key={labelKey}
              className="border-b border-[var(--color-border)] px-1 py-2 text-center text-xs font-medium text-[var(--color-text-muted)]"
            >
              {t(labelKey)}
            </div>
          ))}
        </div>
        {weeks.map((week) => (
          <div key={week[0].dateKey} className="grid grid-cols-7">
            {week.map((day) => {
              const hasEntries = day.entries.length > 0;
              const isSelected = day.dateKey === selectedDateKey;
              return (
                <button
                  type="button"
                  key={day.dateKey}
                  data-testid={`calendar-day-${day.dateKey}`}
                  onClick={() => {
                    setSelectedDateKey(isSelected ? null : day.dateKey);
                  }}
                  className={[
                    'relative flex min-h-[72px] flex-col items-center border-b border-r border-[var(--color-border)] p-1.5 text-left transition-colors',
                    day.isCurrentMonth
                      ? 'text-[var(--color-text-primary)]'
                      : 'text-[var(--color-text-muted)]',
                    isSelected
                      ? 'bg-[var(--color-accent-primary)]/10'
                      : 'hover:bg-[var(--color-bg-base)]',
                  ].join(' ')}
                >
                  <span
                    className={[
                      'flex h-6 w-6 items-center justify-center rounded-full text-xs',
                      day.isToday ? 'bg-[var(--color-accent-primary)] font-bold text-white' : '',
                    ].join(' ')}
                  >
                    {day.date}
                  </span>
                  {hasEntries ? (
                    <span
                      data-testid={`release-count-${day.dateKey}`}
                      className="mt-1 rounded-full bg-[var(--color-accent-primary)] px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white"
                    >
                      {day.entries.length}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        ))}
      </Card>

      {selectedDateKey && selectedEntries.length > 0 ? (
        <div className="mt-6">
          <div className="mb-3 flex items-center gap-3">
            <span className="font-mono text-sm font-semibold text-[var(--color-text-primary)]">
              {selectedDateKey.replace(/-/g, '/')}
            </span>
            <span className="h-px flex-1 bg-[var(--color-border)]" />
            <span className="text-xs text-[var(--color-text-muted)]">
              {t('release.entryCount', { num: String(selectedEntries.length) })}
            </span>
          </div>
          <ul className="flex flex-col gap-3">
            {selectedEntries.map((entry) => (
              <li key={entry.prNumber}>
                <Card className="p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className="rounded-full px-2 py-0.5 text-xs font-medium"
                      style={{
                        color: UX_TINT[entry.uxImpact],
                        border: `1px solid ${UX_TINT[entry.uxImpact]}`,
                      }}
                    >
                      {uxLabel.get(entry.uxImpact) ?? entry.uxImpact}
                    </span>
                    <span className="rounded-full bg-[var(--color-bg-base)] px-2 py-0.5 text-xs text-[var(--color-text-secondary)]">
                      {categoryLabel.get(entry.category) ?? entry.category}
                    </span>
                  </div>
                  <p className="mt-2 text-base font-semibold text-[var(--color-text-primary)]">
                    {entry.summary}
                  </p>
                  <p className="mt-1 text-xs text-[var(--color-text-muted)]">{entry.title}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-[var(--color-text-muted)]">
                    <a
                      href={entry.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 hover:text-[var(--color-accent-primary)]"
                    >
                      <GitMerge className="h-3.5 w-3.5" aria-hidden="true" />#{entry.prNumber}
                    </a>
                    {entry.backlogKey ? (
                      <a
                        href={backlogUrl(entry.backlogKey)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 hover:text-[var(--color-accent-primary)]"
                      >
                        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                        {entry.backlogKey}
                      </a>
                    ) : null}
                    <span>{entry.author}</span>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {selectedDateKey && selectedEntries.length === 0 ? (
        <p className="mt-6 text-center text-sm text-[var(--color-text-muted)]">
          {t('release.noReleasesOnDay')}
        </p>
      ) : null}
    </div>
  );
}
